// A menu of actions: floating by its button on a desktop, an action sheet from the bottom on a phone, with Cancel.
// It's the browser's popover, so a press outside or Escape closes it; arrow keys move through it, and focus goes back
// to its button.
import { useEffect, useId, useLayoutEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from './cx';
import s from './Layout.module.css';

export type MenuEntry =
  | {
      label: string;
      icon?: ReactNode;
      onSelect?: () => void;
      /** Goes somewhere rather than doing something. */
      to?: string;
      destructive?: boolean;
      disabled?: boolean;
    }
  | 'divider';

/**
 * `trigger` draws the button, given what it needs to open the menu: spread `props` on it. Each entry does something,
 * or goes somewhere.
 */
export function Menu({
  label,
  entries,
  trigger,
}: {
  /** What the menu is, for a screen reader: "Actions for R34". */
  label: string;
  entries: readonly MenuEntry[];
  trigger: (props: {
    id: string;
    'aria-haspopup': 'menu';
    'aria-expanded': boolean;
    'aria-controls': string;
    popoverTarget: string;
  }) => ReactNode;
}) {
  const id = useId();
  const menuId = `${id}-menu`;
  const popover = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  // Beside its button: below it, or above when there's no room, and kept on the screen.
  useLayoutEffect(() => {
    const menu = popover.current;
    const by = document.getElementById(id);
    if (!open || !menu || !by || window.matchMedia('(max-width: 719px)').matches) {
      return;
    }
    const b = by.getBoundingClientRect();
    const m = menu.getBoundingClientRect();
    const below = b.bottom + 6 + m.height <= window.innerHeight - 8;
    const top = below ? b.bottom + 6 : Math.max(8, b.top - 6 - m.height);
    const left = Math.min(Math.max(8, b.right - m.width), window.innerWidth - m.width - 8);
    menu.style.top = `${top}px`;
    menu.style.left = `${left}px`;
  }, [open, id]);

  useEffect(() => {
    if (open) {
      popover.current?.querySelector<HTMLElement>('[role="menuitem"]:not(:disabled)')?.focus();
    }
  }, [open]);

  const close = () => {
    popover.current?.hidePopover();
    document.getElementById(id)?.focus();
  };

  const move = (e: KeyboardEvent<HTMLDivElement>) => {
    const items = [
      ...(popover.current?.querySelectorAll<HTMLElement>('[role="menuitem"]:not(:disabled)') ?? []),
    ].filter((el) => el.offsetParent !== null);
    const at = items.indexOf(document.activeElement as HTMLElement);
    const to =
      e.key === 'ArrowDown'
        ? (at + 1) % items.length
        : e.key === 'ArrowUp'
          ? (at - 1 + items.length) % items.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? items.length - 1
              : -1;
    if (to >= 0) {
      e.preventDefault();
      items[to]?.focus();
    } else if (e.key === 'Tab') {
      e.preventDefault();
      close();
    }
  };

  return (
    <>
      {trigger({
        id,
        'aria-haspopup': 'menu',
        'aria-expanded': open,
        'aria-controls': menuId,
        popoverTarget: menuId,
      })}
      <div
        ref={popover}
        id={menuId}
        popover="auto"
        className={s.menu}
        onToggle={(e) => setOpen((e as unknown as { newState: string }).newState === 'open')}
        onKeyDown={move}
      >
        <ul className={s.items} role="menu" aria-label={label}>
          {entries.map((entry, i) =>
            entry === 'divider' ? (
              <li key={`d${i}`} role="separator" className={s.divider} />
            ) : (
              <li key={entry.label} role="none">
                {entry.to ? (
                  <Link
                    role="menuitem"
                    to={entry.to}
                    className={cx(s.item, entry.destructive && s.destructive)}
                    onClick={() => popover.current?.hidePopover()}
                  >
                    {entry.icon}
                    {entry.label}
                  </Link>
                ) : (
                  <button
                    type="button"
                    role="menuitem"
                    disabled={entry.disabled}
                    className={cx(s.item, entry.destructive && s.destructive)}
                    onClick={() => {
                      close();
                      entry.onSelect?.();
                    }}
                  >
                    {entry.icon}
                    {entry.label}
                  </button>
                )}
              </li>
            ),
          )}
        </ul>
        <button type="button" className={cx(s.item, s.cancel)} onClick={close}>
          Cancel
        </button>
      </div>
    </>
  );
}
